import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, Package, Droplets, Users, Database, AlertTriangle, Tag, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { smartDisplayQty } from '@/lib/unitConverter';
import { useKurs } from '@/hooks/useKurs';
import LastUpdated from '@/components/ui/LastUpdated';
import PackagingForm from '@/components/stok/PackagingForm';
import StokBahanForm from '@/components/stok/StokBahanForm';
import ItemTambahanForm from '@/components/stok/ItemTambahanForm';
import { useState } from 'react';
import { useSession } from '@/lib/SessionContext';
import { getShowPriceAccess } from '@/lib/AuthSession';

const fmt = (n) => n != null ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '—';

export default function StokItemDetail() {
  const urlParams = new URLSearchParams(window.location.search);
  const itemId = urlParams.get('id');
  const rawTipe = urlParams.get('tipe');
  const tipe = rawTipe === 'bahan-cair' ? 'bahan' : rawTipe; // botol, tutup, spray, bahan, item-tambahan
  const { kurs, kursEuro } = useKurs();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const { member } = useSession();

  const portal = useMemo(() => {
    const match = window.location.pathname.match(/\/office\/([^\/]+)/);
    return match ? match[1] : 'finance';
  }, []);
  const isProductionView = portal === 'production';

  // Load data berdasarkan tipe
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const { data: bahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: orders = [] } = useQuery({ queryKey: ['production-orders'], queryFn: () => base44.entities.ProductionOrder.list('brand_name') });
  const { data: racikanDb = [] } = useQuery({ queryKey: ['racikan-database'], queryFn: () => base44.entities.RacikanDatabase.list('nama_brand') });
  const { data: bahanDb = [] } = useQuery({ queryKey: ['database-bahan'], queryFn: () => base44.entities.DatabaseBahan.list('nama_brand') });

  const delBotol = useMutation({ mutationFn: id => base44.entities.Botol.delete(id), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['botol'] }); window.history.back(); } });
  const delTutup = useMutation({ mutationFn: id => base44.entities.Tutup.delete(id), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['tutup'] }); window.history.back(); } });
  const delSpray = useMutation({ mutationFn: id => base44.entities.Spray.delete(id), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['spray'] }); window.history.back(); } });
  const delFullset = useMutation({ mutationFn: id => base44.entities.Fullset.delete(id), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['fullset'] }); window.history.back(); } });
  const delBahan = useMutation({ mutationFn: id => base44.entities.BahanCair.delete(id), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['bahan-cair'] }); window.history.back(); } });
  const delItem = useMutation({ mutationFn: id => base44.entities.ItemTambahan.delete(id), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['item-tambahan'] }); window.history.back(); } });

  // Cari item
  const item = useMemo(() => {
    if (!itemId || !tipe) return null;
    if (tipe === 'botol') return botolList.find(i => i.id === itemId) || null;
    if (tipe === 'tutup') return tutupList.find(i => i.id === itemId) || null;
    if (tipe === 'spray') return sprayList.find(i => i.id === itemId) || null;
    if (tipe === 'fullset') return fullsetList.find(i => i.id === itemId) || null;
    if (tipe === 'bahan' || tipe === 'bahan-cair') return bahanList.find(i => i.id === itemId) || null;
    if (tipe === 'item-tambahan') return itemTambahanList.find(i => i.id === itemId) || null;
    return null;
  }, [itemId, tipe, botolList, tutupList, sprayList, fullsetList, bahanList, itemTambahanList]);

  const isMatchItem = (targetItem, candidateName, candidateVendor, candidateId) => {
    if (!targetItem) return false;
    if (candidateId && (
      String(candidateId) === String(targetItem.id) || 
      String(candidateId) === `bahan-${targetItem.id}` || 
      String(candidateId) === `botol-${targetItem.id}` ||
      String(candidateId) === `tutup-${targetItem.id}` ||
      String(candidateId) === `spray-${targetItem.id}` ||
      String(candidateId) === `fullset-${targetItem.id}`
    )) {
      return true;
    }
    const targetName = (targetItem.nama || '').toLowerCase().trim();
    const cName = (candidateName || '').toLowerCase().trim();
    if (!targetName || !cName) return false;

    const targetVendor = (targetItem.vendor || '').toLowerCase().trim();
    const cVendor = (candidateVendor || '').toLowerCase().trim();

    // Jika keduanya punya vendor dan vendornya BEDA -> BUKAN barang yang sama!
    if (targetVendor && cVendor && targetVendor !== cVendor) {
      return false;
    }

    // Exact name match
    if (targetName === cName) return true;

    // Jika vendor cocok dan sama persis
    if (targetVendor && cVendor && targetVendor === cVendor) {
      if (cName.includes(targetName) || targetName.includes(cName)) return true;
    }

    return false;
  };

  // Brand usage (PO yang pakai)
  const brandUsage = useMemo(() => {
    if (!item) return [];
    const brandMap = {};
    orders.forEach(order => {
      const brand = order.brand_name || 'Unknown';
      let used = false;
      
      // 1. Cek dari components (botol, tutup, spray, packaging)
      (order.components || []).forEach(comp => {
        if (isMatchItem(item, comp.item_name, comp.vendor, comp.inventory_item_id)) {
          used = true;
        }
      });
      
      // 2. Cek dari racikan_digunakan & products.racikan (bahan cair)
      if (!used && (tipe === 'bahan' || tipe === 'bahan-cair')) {
        (order.racikan_digunakan || []).forEach(r => {
          if (isMatchItem(item, r.nama_bahan || r.bahan_nama, r.vendor, r.material_id || r.id)) {
            used = true;
          }
        });
        if (!used) {
          (order.products || []).forEach(p => {
            (p.racikan || []).forEach(r => {
              if (isMatchItem(item, r.nama_bahan || r.bahan_nama, r.vendor, r.material_id || r.id)) {
                used = true;
              }
            });
          });
        }
      }

      // 3. Cek dari additional_components (item tambahan seperti stiker, box, dll)
      if (!used && tipe === 'item-tambahan') {
        (order.additional_components || []).forEach(comp => {
          if (isMatchItem(item, comp.name || comp.type, comp.vendor, comp.id)) {
            used = true;
          }
        });
      }
      
      if (used) {
        brandMap[brand] = (brandMap[brand] || 0) + 1;
      }
    });
    return Object.entries(brandMap).map(([brand, count]) => ({ brand, count })).sort((a, b) => b.count - a.count);
  }, [orders, item, tipe]);

  // Database usage
  const dbUsage = useMemo(() => {
    if (!item) return [];
    const results = [];
    if (tipe === 'bahan' || tipe === 'bahan-cair') {
      racikanDb.forEach(db => {
        const found = (db.racikan || []).some(r => 
          isMatchItem(item, r.nama_bahan || r.bahan_nama, r.vendor, r.id)
        );
        if (found) results.push({ brand: db.nama_brand, varian: db.nama_varian, source: 'Racikan DB' });
      });
      bahanDb.forEach(db => {
        const found = (db.racikan || []).some(r => 
          isMatchItem(item, r.nama_bahan || r.bahan_nama, r.vendor, r.id)
        );
        if (found) results.push({ brand: db.nama_brand, varian: db.nama_varian, source: 'Database Bahan' });
      });
    } else {
      bahanDb.forEach(db => {
        const fields = [
          { name: db.botol_nama, vendor: db.botol_vendor },
          { name: db.tutup_nama, vendor: db.tutup_vendor },
          { name: db.spray_nama, vendor: db.spray_vendor },
          { name: db.fullset_nama, vendor: db.fullset_vendor }
        ];
        const found = fields.some(f => isMatchItem(item, f.name, f.vendor));
        if (found) results.push({ brand: db.nama_brand, varian: db.nama_varian, source: 'Database Bahan' });
      });
    }
    return results;
  }, [racikanDb, bahanDb, item, tipe]);

  // Group database usage by brand to prevent long messy lists
  const groupedDbUsage = useMemo(() => {
    const map = {};
    dbUsage.forEach(u => {
      if (!map[u.brand]) {
        map[u.brand] = { brand: u.brand, variants: [] };
      }
      if (!map[u.brand].variants.some(v => v.varian === u.varian)) {
        map[u.brand].variants.push({ varian: u.varian, source: u.source });
      }
    });
    return Object.values(map);
  }, [dbUsage]);

  const handleDelete = () => {
    if (!item || !confirm(`Hapus ${item.nama}?`)) return;
    if (tipe === 'botol') delBotol.mutate(item.id);
    else if (tipe === 'tutup') delTutup.mutate(item.id);
    else if (tipe === 'spray') delSpray.mutate(item.id);
    else if (tipe === 'fullset') delFullset.mutate(item.id);
    else if (tipe === 'bahan') delBahan.mutate(item.id);
    else if (tipe === 'item-tambahan') delItem.mutate(item.id);
  };

  const handleSaved = () => {
    setShowForm(false);
    ['botol', 'tutup', 'spray', 'fullset', 'bahan-cair', 'item-tambahan'].forEach(k => queryClient.invalidateQueries({ queryKey: [k] }));
  };

  const isLoading = !itemId || !tipe || (
    (tipe === 'botol' && !botolList.length && !bahanList.length) ||
    false
  );

  if (!item && itemId) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Item tidak ditemukan</p>
        <Link to={`/office/${portal}/inventory`}>
          <Button variant="outline" className="mt-4">Kembali ke Stok & Inventori</Button>
        </Link>
      </div>
    );
  }

  if (!item) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
    </div>
  );

  const isLow = tipe === 'bahan'
    ? (item.stok != null && item.stok < 0.5)
    : (item.stok != null && item.stok < 10);

  const tipeLabel = tipe === 'botol' ? 'Botol' : tipe === 'tutup' ? 'Tutup' : tipe === 'spray' ? 'Spray' : tipe === 'fullset' ? 'Fullset' : tipe === 'bahan' ? 'Bahan Cair' : 'Item Tambahan';
  const tipeIcon = tipe === 'bahan' ? <Droplets className="w-5 h-5" /> : tipe === 'item-tambahan' ? <Tag className="w-5 h-5" /> : <Package className="w-5 h-5" />;

  const backUrl = `/office/${portal}/inventory?tab=${tipe === 'bahan' ? 'bahan' : tipe === 'item-tambahan' ? 'item-tambahan' : 'packaging'}`;

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Back + Actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground" onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = backUrl}>
          <ArrowLeft className="w-4 h-4" /> Kembali
        </Button>
        {!isProductionView && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowForm(true)}>
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive" onClick={handleDelete}>
              <Trash2 className="w-3.5 h-3.5" /> Hapus
            </Button>
          </div>
        )}
      </div>

      {/* Header Card */}
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex items-start gap-3 md:gap-4">
            <div className={`p-2.5 md:p-3 rounded-xl shrink-0 ${tipe === 'bahan' ? 'bg-purple-100 text-purple-700' : tipe === 'item-tambahan' ? 'bg-pink-100 text-pink-700' : 'bg-primary/10 text-primary'}`}>
              {tipeIcon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge variant="outline" className="text-xs">{tipeLabel}</Badge>
                {item.kategori && <Badge variant="outline" className="text-xs">{item.kategori}</Badge>}
                {item.grade && <Badge className="text-xs bg-amber-100 text-amber-700">{item.grade}</Badge>}
                {tipe === 'botol' && item.ukuran_label_ml && <Badge variant="outline" className="text-xs">{item.ukuran_label_ml}ml</Badge>}
                {tipe === 'spray' && item.tipe && <Badge className="text-xs bg-green-100 text-green-700">{item.tipe}</Badge>}
              </div>
              <h1 className="font-display text-xl md:text-2xl font-bold tracking-tight">{item.nama}</h1>
              {item.vendor && <p className="text-xs md:text-sm text-muted-foreground mt-0.5 md:mt-1">Vendor: <span className="text-foreground font-medium">{item.vendor}</span></p>}
              {item.catatan_teknis && <p className="text-xs text-amber-600 mt-1 italic flex items-center gap-1"><FileText className="w-3 h-3 shrink-0" />{item.catatan_teknis}</p>}
              {item.catatan && <p className="text-xs text-muted-foreground mt-1 italic">{item.catatan}</p>}
              <div className="mt-2"><LastUpdated date={item.updated_date || item.created_date} /></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stok & Harga */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
        <Card className={isLow ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 mb-1">
              {isLow && <AlertTriangle className="w-4 h-4 text-red-600" />}
              <p className="text-xs font-semibold text-muted-foreground uppercase">Stok Saat Ini</p>
            </div>
            {tipe === 'bahan' ? (
              <p className={`text-2xl font-bold ${isLow ? 'text-red-600' : ''}`}>
                {(() => { const { display, unit } = smartDisplayQty(item.stok, item.satuan); return `${display} ${unit}`; })()}
              </p>
            ) : (
              <p className={`text-2xl font-bold ${isLow ? 'text-red-600' : ''}`}>{item.stok ?? '—'} pcs</p>
            )}
            {isLow && <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Stok menipis</p>}
          </CardContent>
        </Card>

        {!isProductionView && getShowPriceAccess(member, "stok_inventori") && (item.harga_rupiah || item.harga_dollar || item.harga_euro) && (
          <Card>
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Harga Beli</p>
                {item.mata_uang === 'USD' ? (
                  <Badge className="text-[10px] font-black bg-amber-50 text-[#8C6208] border border-amber-300 shadow-none">USD ($)</Badge>
                ) : item.mata_uang === 'EUR' ? (
                  <Badge className="text-[10px] font-black bg-blue-50 text-blue-700 border border-blue-300 shadow-none">EUR (€)</Badge>
                ) : (
                  <Badge className="text-[10px] font-black bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-none">IDR (Rp)</Badge>
                )}
              </div>
              {item.mata_uang === 'EUR' && item.harga_euro ? (
                <>
                  <p className="text-lg font-bold text-blue-700">€{item.harga_euro}</p>
                  <p className="text-xs text-muted-foreground">≈ Rp {fmt(item.harga_rupiah)}/{item.satuan || 'pcs'}</p>
                </>
              ) : (item.mata_uang === 'USD' && item.harga_dollar) ? (
                <>
                  <p className="text-lg font-bold text-[#8C6208]">${item.harga_dollar}</p>
                  <p className="text-xs text-muted-foreground">≈ Rp {fmt(item.harga_rupiah)}/{item.satuan || 'pcs'}</p>
                </>
              ) : (
                <div>
                  <p className="text-lg font-bold text-slate-800">Rp {fmt(item.harga_rupiah)}<span className="text-sm font-normal text-muted-foreground">/{item.satuan || 'pcs'}</span></p>
                  {item.harga_dollar && (
                    <p className="text-xs text-muted-foreground mt-0.5">≈ ${Number(item.harga_dollar).toFixed(2)} (acuan)</p>
                  )}
                </div>
              )}
              <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[10px] text-slate-500 font-medium">Status PPN</span>
                {item.ppn_type === 'exclude' ? (
                  <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 font-bold">
                    Exclude PPN (+{item.ppn_percentage || 11}%)
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 font-bold">
                    Include PPN
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {tipe === 'botol' && item.ukuran_aktual_ml && item.ukuran_aktual_ml !== item.ukuran_label_ml && (
          <Card>
            <CardContent className="p-3 md:p-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Ukuran Aktual</p>
              <p className="text-xl font-bold">{item.ukuran_aktual_ml}ml</p>
              <p className="text-xs text-muted-foreground">Label: {item.ukuran_label_ml}ml</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Brand PO yang pakai */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-sm md:text-base">Dipakai di PO Brand</CardTitle>
            <Badge variant="secondary" className="ml-auto">{brandUsage.length} brand</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          {brandUsage.length === 0 ? (
            <p className="text-xs md:text-sm text-muted-foreground text-center py-4">Belum terdeteksi di PO aktif</p>
          ) : (
            <div className="space-y-2">
              {brandUsage.map(({ brand, count }) => (
                <div key={brand} className="flex items-center justify-between px-3 py-2.5 bg-muted/40 rounded-xl">
                  <span className="font-medium text-sm">{brand}</span>
                  <Badge className="bg-primary/10 text-primary border-primary/20">{count}× PO</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Terdaftar di Database */}
      {groupedDbUsage.length > 0 && (
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <CardHeader className="pb-3 border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-[#8C6208]" />
              <CardTitle className="text-sm md:text-base font-black text-slate-800">Terdaftar di Database Brand</CardTitle>
              <Badge className="bg-[#8C6208]/10 text-[#8C6208] border-none ml-auto font-bold text-[10px] md:text-xs">
                {dbUsage.length} Link Varian
              </Badge>
            </div>
            <p className="text-[9px] md:text-[11px] text-muted-foreground mt-0.5">
              Klik nama varian di bawah ini untuk berpindah langsung ke Database Formula & Kemasan yang bersangkutan.
            </p>
          </CardHeader>
          <CardContent className="p-3 md:p-4">
            <div className="space-y-3 md:space-y-4 max-h-[250px] overflow-y-auto pr-2">
              {groupedDbUsage.map((group, i) => (
                <div key={i} className="p-3 md:p-4 bg-slate-50 border border-slate-200 rounded-xl md:rounded-2xl space-y-2 md:space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-[#8C6208] tracking-tight">{group.brand}</span>
                    <Badge variant="outline" className="text-[10px] bg-white border-slate-200 text-slate-500 font-bold px-2 py-0.5">
                      {group.variants.length} Varian
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {group.variants.map((v, idx) => (
                      <Link
                        key={idx}
                        to={`/office/production/brands?brand=${encodeURIComponent(group.brand)}&variant=${encodeURIComponent(v.varian)}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-[#F9F5EC] border border-slate-200 hover:border-[#E6D5B8] text-xs font-bold text-slate-700 hover:text-[#8C6208] rounded-xl shadow-xs transition-all group"
                      >
                        <span>{v.varian}</span>
                        <span className="text-[9px] bg-slate-100 text-slate-500 group-hover:bg-[#E6D5B8]/40 group-hover:text-[#8C6208] px-1 py-0.2 rounded font-semibold">{v.source === 'Racikan DB' ? 'Formula' : 'Kemasan'}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Form */}
      {showForm && tipe === 'packaging' && (
        <PackagingForm item={item} tipeAwal={tipe} kurs={kurs} onClose={() => setShowForm(false)} onSaved={handleSaved} />
      )}
      {showForm && (tipe === 'botol' || tipe === 'tutup' || tipe === 'spray' || tipe === 'fullset') && (
        <PackagingForm item={item} tipeAwal={tipe} kurs={kurs} onClose={() => setShowForm(false)} onSaved={handleSaved} />
      )}
      {showForm && tipe === 'bahan' && (
        <StokBahanForm item={item} kurs={kurs} onClose={() => setShowForm(false)} onSaved={handleSaved} />
      )}
      {showForm && tipe === 'item-tambahan' && (
        <ItemTambahanForm item={item} onClose={() => setShowForm(false)} onSaved={handleSaved} />
      )}
    </div>
  );
}